import { PushPublicKeyResponse, type PushUnsubscribeInput } from '@rp/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { deviceStorage } from '../../design/typeScale'
import { useReadySession } from '../../session/useSession'
import { currentPushSubscription, pushSupported, subscribePush } from './pushBrowser'

export const PUSH_PUBLIC_KEY_PATH = '/v1/push/public-key'
export const PUSH_SUBSCRIPTIONS_PATH = '/v1/push/subscriptions'

const PushAck = z.unknown()
const NO_ENDPOINT = ''

export interface PushDeviceScope {
  userId: string
  tenantId: string
}

const pushKeys = {
  device: (scope: PushDeviceScope | null) => ['push', 'device', scope?.userId ?? '', scope?.tenantId ?? ''] as const,
  devicePrefix: ['push', 'device'] as const,
  publicKey: (tenantId: string) => ['push', 'public-key', tenantId] as const,
}

const registryKey = (userId: string, tenantId: string) => `rp.push.${userId}.${tenantId}`

const readRegistered = (key: string): string => {
  try {
    return deviceStorage().getItem(key) ?? NO_ENDPOINT
  } catch {
    return NO_ENDPOINT
  }
}

const storeRegistered = (key: string, endpoint: string): void => {
  try {
    deviceStorage().setItem(key, endpoint)
  } catch {
    return
  }
}

const usePushScope = () => {
  const { api, me, membership } = useReadySession()
  const userId = me.user.id
  const tenantId = membership.tenantId
  return { api, tenantId, scope: { userId, tenantId }, registry: registryKey(userId, tenantId) }
}

const subscribedForScope = async (scope: PushDeviceScope): Promise<boolean> => {
  const subscription = await currentPushSubscription()
  return subscription !== null && subscription.endpoint === readRegistered(registryKey(scope.userId, scope.tenantId))
}

export const useDevicePushSubscribed = (scope: PushDeviceScope | null) =>
  useQuery({
    queryKey: pushKeys.device(scope),
    enabled: pushSupported() && scope !== null,
    queryFn: async () => (scope === null ? false : subscribedForScope(scope)),
  })

export const usePushPublicKey = () => {
  const { api, tenantId } = usePushScope()
  return useQuery({
    queryKey: pushKeys.publicKey(tenantId),
    enabled: pushSupported(),
    queryFn: async () => (await api.request(PushPublicKeyResponse, { method: 'GET', path: PUSH_PUBLIC_KEY_PATH })).data.publicKey,
  })
}

export const usePushEnabled = () => useDevicePushSubscribed(usePushScope().scope)

export const useEnablePush = () => {
  const { api, registry } = usePushScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (publicKey: string) => {
      const input = await subscribePush(publicKey)
      await api.request(PushAck, { method: 'PUT', path: PUSH_SUBSCRIPTIONS_PATH, body: input })
      storeRegistered(registry, input.endpoint)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: pushKeys.devicePrefix }),
  })
}

export const useDisablePush = () => {
  const { api, registry } = usePushScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const subscription = await currentPushSubscription()
      const endpoint = subscription?.endpoint ?? readRegistered(registry)
      if (endpoint !== NO_ENDPOINT) {
        const body: PushUnsubscribeInput = { endpoint }
        await api.request(PushAck, { method: 'DELETE', path: PUSH_SUBSCRIPTIONS_PATH, body })
      }
      storeRegistered(registry, NO_ENDPOINT)
      await subscription?.unsubscribe()
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: pushKeys.devicePrefix }),
  })
}
